import React, { useMemo } from "react";
import { GraphvizViewer } from "../Graphviz/GraphvizViewer";
import { GroundedResidualSystem } from "../../powerFlow/groundedProblem";
import { residualsToDot, residualGraphStats } from "../../powerFlow/nonlinear/residualGraph";

interface Props {
  system?: GroundedResidualSystem;
}

const swatch = (fill: string): React.CSSProperties => ({
  display: "inline-block",
  width: 12,
  height: 12,
  background: fill,
  border: "1px solid #cbd5e1",
  borderRadius: 2,
  marginRight: 4,
  verticalAlign: "middle",
});

/**
 * The AC residual system as a shared expression DAG — the nonlinear counterpart
 * of the DCOPF optimization graph. Each residual's `= 0` root feeds an
 * expression graph over the decision variables, structurally hash-consed so a
 * variable or repeated subexpression is drawn once with fan-out.
 */
const ResidualGraph: React.FC<Props> = ({ system }) => {
  const { dot, stats } = useMemo(() => {
    if (!system) return { dot: "", stats: null };
    return {
      dot: residualsToDot(system.residuals),
      stats: residualGraphStats(system.residuals),
    };
  }, [system]);

  if (!system) {
    return (
      <div style={{ padding: "16px", color: "#666", fontStyle: "italic" }}>
        No residual system — run an AC power flow to see its graph.
      </div>
    );
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ marginBottom: "8px" }}>
        <h4 style={{ margin: "0 0 4px 0", fontSize: "14px" }}>Residual graph</h4>
        <div style={{ fontSize: "12px", color: "#666" }}>
          {system.residuals.length} residuals over {system.variables.length} variables
          {stats && (
            <>
              {" "}
              · {stats.treeNodes} expression nodes shared down to {stats.dagNodes} (
              {Math.round((1 - stats.dagNodes / stats.treeNodes) * 100)}% shared)
            </>
          )}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <GraphvizViewer dotSource={dot} style={{ width: "100%", height: "100%" }} />
      </div>

      <div
        style={{
          marginTop: "8px",
          padding: "8px",
          background: "#f8f8f8",
          borderRadius: "4px",
          fontSize: "12px",
        }}
      >
        <span style={{ marginRight: 12 }}>
          <span style={swatch("#fef3c7")} />residual (= 0)
        </span>
        <span style={{ marginRight: 12 }}>
          <span style={swatch("#dbeafe")} />variable
        </span>
        <span style={{ marginRight: 12 }}>
          <span style={swatch("#ede9fe")} />sin / cos / sqrt
        </span>
        <span>
          <span style={swatch("#ffffff")} />operator / constant
        </span>
      </div>
    </div>
  );
};

export default ResidualGraph;
